import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ArrowRightIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { rankSteps, useRankLookup } from "@/features/matches";
import { cn } from "@/shared/lib";
import { Badge } from "@/shared/ui/badge";

import type { PlayerSummary } from "../../model/PlayerSummary";
import { rankPath } from "../../model/rankPath";
import { ProfileSection } from "./ProfileSection";
import { RankPoint } from "./RankPoint";

// Сколько ждать, прежде чем закрыть карточку, когда мышь ушла с точки: успеть дойти до окна.
const HOVER_CLOSE_DELAY_MS = 200;

const UP = msg({ message: "{steps, plural, one {▲ # subrank} other {▲ # subranks}}" });
const DOWN = msg({ message: "{steps, plural, one {▼ # subrank} other {▼ # subranks}}" });
const SUMMARY = msg({
  message:
    "Rank after each ranked match from {from} to {to}: from {first} to {last}, {wins, plural, one {# win} other {# wins}} and {losses, plural, one {# loss} other {# losses}}.",
});

// Доля высоты графика → `top` внутри области с отступом 10px сверху и снизу.
const top = (share: number) => `${String(share * 100)}%`;

type RankPathProps = {
  // Ранг после последних рейтинговых матчей, от старого к новому.
  history: PlayerSummary["rank_history"];
};

// «Путь по рангам»: ранг после каждого рейтингового матча плавной линией, точки — победа или
// поражение (наведение или нажатие — карточка матча), слева — тиры со значками, пунктир —
// следующий подуровень. Калибровки нет:
// ранг после матча история знает только вне её.
export const RankPath = ({ history }: RankPathProps) => {
  const { i18n, t } = useLingui();
  const rankLookup = useRankLookup();
  // Открытая карточка и как её открыли: наведением мыши фокус не переносится в окно.
  const [opened, setOpened] = useState<{ matchId: number; byHover: boolean } | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  useEffect(
    () => () => {
      window.clearTimeout(closeTimer.current);
    },
    [],
  );
  const hover = (matchId: number, hovering: boolean) => {
    window.clearTimeout(closeTimer.current);
    if (hovering) {
      setOpened((current) => (current?.matchId === matchId ? current : { matchId, byHover: true }));
    } else {
      closeTimer.current = window.setTimeout(() => {
        setOpened((current) => (current?.matchId === matchId ? null : current));
      }, HOVER_CLOSE_DELAY_MS);
    }
  };
  const path = rankPath(history);
  const title = <Trans>Rank path</Trans>;
  const first = path?.points[0];
  const last = path?.points.at(-1);
  if (path === null || first === undefined || last === undefined) {
    return (
      <ProfileSection title={title}>
        <p className="text-sm text-muted">
          <Trans>
            Your rank after each ranked match will show up here. Calibration matches do not count.
          </Trans>
        </p>
      </ProfileSection>
    );
  }

  const day = new Intl.DateTimeFormat(i18n.locale, { day: "numeric", month: "short" });
  const firstRank = rankLookup(first.rank_badge).name;
  const lastRank = rankLookup(last.rank_badge).name;
  const steps = rankSteps(first.rank_badge, last.rank_badge);
  const wins = path.points.filter((point) => point.won).length;
  const goal = path.goal === null ? null : rankLookup(path.goal.badge).name;

  return (
    <ProfileSection
      title={title}
      aside={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5 text-sm text-muted">
            {firstRank}
            <ArrowRightIcon aria-hidden="true" className="size-3.5 text-subtle" />
            <span className="font-semibold text-foreground">{lastRank}</span>
          </span>
          {steps !== 0 && (
            <Badge
              variant={steps > 0 ? "good" : "bad"}
              className="rounded-full px-2.5 font-sans text-[13px] font-semibold"
            >
              {i18n._({ ...(steps > 0 ? UP : DOWN), values: { steps: Math.abs(steps) } })}
            </Badge>
          )}
        </span>
      }
    >
      <div className="flex gap-3">
        <div className="relative h-[230px] w-28 shrink-0">
          <div className="absolute inset-x-0 inset-y-2.5">
            {path.tiers.map((tier) => {
              const rank = rankLookup(tier.tier * 10);
              const [from, to] = tier.subranks;
              return (
                <span
                  key={tier.tier}
                  className="absolute inset-x-0 flex -translate-y-1/2 items-center gap-2"
                  style={{ top: top(tier.y) }}
                >
                  {rank.iconUrl !== null && (
                    <img src={rank.iconUrl} alt="" className="size-7 shrink-0 object-contain" />
                  )}
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-[13px] font-semibold">{rank.name}</span>
                    <span className="text-[11px] text-subtle">
                      {from === to ? String(from) : `${String(from)}–${String(to)}`}
                    </span>
                  </span>
                </span>
              );
            })}
          </div>
        </div>
        <div
          role="group"
          aria-label={i18n._({
            ...SUMMARY,
            values: {
              from: day.format(new Date(first.started_at)),
              to: day.format(new Date(last.started_at)),
              first: firstRank,
              last: lastRank,
              wins,
              losses: path.points.length - wins,
            },
          })}
          className="relative h-[230px] min-w-0 grow"
        >
          <div className="absolute inset-x-0 inset-y-2.5">
            {path.levels.map((level) => (
              <span
                key={level.badge}
                className={cn(
                  "absolute inset-x-0 border-t",
                  level.tierStart ? "border-border" : "border-surface-raised",
                )}
                style={{ top: top(level.y) }}
              />
            ))}
            {path.goal !== null && (
              <span
                data-goal={path.goal.badge}
                className="absolute inset-x-0 border-t border-dashed border-muted"
                style={{ top: top(path.goal.y) }}
              />
            )}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              className="absolute inset-0 size-full overflow-visible"
            >
              <path
                d={path.linePath}
                fill="none"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                className="stroke-accent"
              />
            </svg>
            {path.points.map((point) => (
              <RankPoint
                key={point.match_id}
                matchId={point.match_id}
                won={point.won}
                label={`${day.format(new Date(point.started_at))} · ${
                  point.won ? t`win` : t`loss`
                } · ${rankLookup(point.rank_badge).name}`}
                x={point.x}
                y={point.y}
                open={opened?.matchId === point.match_id}
                openedByHover={opened?.matchId === point.match_id && opened.byHover}
                onHover={(hovering) => {
                  hover(point.match_id, hovering);
                }}
                onOpenChange={(open) => {
                  window.clearTimeout(closeTimer.current);
                  setOpened(open ? { matchId: point.match_id, byHover: false } : null);
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-4 pl-[124px] text-xs text-subtle">
        <span>{day.format(new Date(first.started_at))}</span>
        <span>{day.format(new Date(last.started_at))}</span>
      </div>
      <div className="-mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1 pl-[124px] text-xs text-subtle">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-2.5 rounded-full bg-good" />
          <Trans>win</Trans>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-2.5 rounded-full bg-bad" />
          <Trans>loss</Trans>
        </span>
        {goal !== null && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="w-4 border-t border-dashed border-muted" />
            <Trans>next: {goal}</Trans>
          </span>
        )}
      </div>
    </ProfileSection>
  );
};
